/**
 * Small on/off for the CME MES candle overlay (MNQ/NQ price pane, issue #620),
 * the twin of VestCandleToggle and stacked just above it: faint until hovered,
 * 32px hit area, a small "ES" tag on the icon. While on it also shows the
 * MNQ/MES ratio the newest drawn candle was scaled by ("ES x3.68").
 * State: mesCandleOverlayStore (persisted, off by default), independent of
 * the Vest overlay.
 */
import React from 'react';
import { CandlestickChart } from 'lucide-react';
import { useMesCandleOverlayStore } from '../../store/mesCandleOverlayStore';
import { ratioLabel, RATIO_MAX_AGE_MS, RATIO_MIN_SAMPLES, RATIO_WINDOW } from '../../utils/mesCandles';

export interface MesCandleToggleProps {
  /** Ratio applied to the newest drawn MES candle; null = none drawn yet. */
  ratio: number | null;
}

export const MesCandleToggle: React.FC<MesCandleToggleProps> = ({ ratio }) => {
  const enabled = useMesCandleOverlayStore((s) => s.enabled);
  const toggle = useMesCandleOverlayStore((s) => s.toggle);
  const label = enabled ? 'ES candles: hide the MES overlay' : 'ES candles: show CME MES candles over the chart, scaled by the MNQ/MES ratio';
  const ratioTitle =
    ratio === null
      ? `No ES candles yet: waiting for MES candles and at least ${RATIO_MIN_SAMPLES} 2m bars closed on both contracts`
      : `MES prices x ${ratio.toFixed(4)} onto this chart's axis (median chart close / MES close over the last ${RATIO_WINDOW} 2m bars closed on both, before each bar, within ${RATIO_MAX_AGE_MS / 3_600_000}h)`;
  return (
    <div className={`vest-candle-toggle mes-candle-toggle ${enabled ? 'is-on' : ''}`} data-testid="mes-candle-toggle-wrap">
      <button
        type="button"
        className="vest-candle-toggle-btn mes-candle-toggle-btn"
        data-testid="mes-candle-toggle"
        aria-label={label}
        aria-pressed={enabled}
        title={label}
        // Keep the tap off the chart below (pan, long-press alert, crosshair).
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
      >
        <CandlestickChart size={13} />
        <span className="mes-candle-toggle-tag" aria-hidden="true">ES</span>
      </button>
      {enabled && (
        <span className="vest-candle-gap mes-candle-ratio" data-testid="mes-candle-ratio" title={ratioTitle}>
          {ratioLabel(ratio)}
        </span>
      )}
    </div>
  );
};
